import React, { useCallback } from 'react';
import type { Editor } from '@tiptap/core';
import {
	AvailableTools,
	FIXED_CONTAINER_ID,
	IconFont,
	MergeTagBadge,
	useEditorProps,
} from '@bay-email/editor';
import { t } from '@bay-email/core';
import { RICH_TEXT_TOOL_BAR } from '@extensions/constants';
import { ToolItem } from '../ToolItem';
import { Link, LinkParams } from '../Link';
import { Unlink } from '../Unlink';
import { FontFamily } from '../FontFamily';
import { FontSize } from '../FontSize';
import { MergeTags } from '../MergeTags';
import { TextTransform } from '../TextTransform';
import { TextWrap } from '../TextWrap';
import { Bold } from '../Bold';
import { Italic } from '../Italic';
import { StrikeThrough } from '../StrikeThrough';
import { Underline } from '../Underline';
import { IconFontColor } from './IconFontColor';
import { IconBgColor } from './IconBgColor';
import { TableCellBgColor } from './TableCellBgColor';
import { LineHeightTool } from './LineHeightTool';
import { LetterSpacingTool } from './LetterSpacingTool';
import { DEFAULT_RTE_TOOLS, ensurePreviewFontLoaded, useBrandColors } from './toolbarConfig';
import { TiptapEditorContext, useTiptapEditorSync } from './TiptapEditorContext';
import {
	DYNAMIC_BACKGROUND_COLOR_ATTR,
	DYNAMIC_COLOR_ATTR,
} from '../../../TiptapEditable/extensions';

export { TiptapEditorContext } from './TiptapEditorContext';

// "{{user.brandPrimaryColor}}" -> "user.brandPrimaryColor": the key is stored
// brace-less because a bare {{...}} in editor HTML is shattered by
// MergeTagBadge.transform.
function mergeTagKeyOf(dynamicTag: string) {
	return dynamicTag.replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '').trim();
}

// The colour tools read their current value from a DOM range, so hand them one
// built from the editor selection instead of editing them.
function getSelectionDomRange(editor: Editor): Range | null {
	try {
		const { from, to } = editor.state.selection;
		const start = editor.view.domAtPos(from);
		const end = editor.view.domAtPos(to);
		const range = document.createRange();
		range.setStart(start.node, start.offset);
		range.setEnd(end.node, end.offset);
		return range;
	} catch {
		return null;
	}
}

const getPopoverMountNode = () => document.getElementById(FIXED_CONTAINER_ID)!;

export function TiptapTools({ editor }: { editor: Editor }) {
	const { enabledMergeTagsBadge, toolbar } = useEditorProps();
	useTiptapEditorSync(editor);

	const brandColors = useBrandColors();

	const execCommand = useCallback(
		(cmd: string, val?: any, opts?: { dynamicTag?: string }) => {
			const chain = () => editor.chain().focus();
			const dynamicKey = opts?.dynamicTag ? mergeTagKeyOf(opts.dynamicTag) : null;

			switch (cmd) {
				case 'bold':
					chain().toggleBold().run();
					break;
				case 'italic':
					chain().toggleItalic().run();
					break;
				case 'underline':
					chain().toggleUnderline().run();
					break;
				case 'strikeThrough':
					chain().toggleStrike().run();
					break;
				case 'fontName': {
					const fontFamily = String(val || '').trim();
					if (!fontFamily) break;
					ensurePreviewFontLoaded(fontFamily);
					chain().setMark('textStyle', { fontFamily }).run();
					break;
				}
				case 'fontSizePx': {
					const parsed = Number.parseInt(String(val), 10);
					if (Number.isNaN(parsed)) break;
					const sizePx = Math.max(8, Math.min(parsed, 300));
					chain().setMark('textStyle', { fontSize: `${sizePx}px` }).run();
					break;
				}
				case 'textTransform':
					chain()
						.setMark('textStyle', {
							textTransform: String(val || 'initial').trim() || 'initial',
						})
						.run();
					break;
				case 'textWrap':
					chain()
						.setMark('textStyle', {
							whiteSpace: String(val || 'normal').trim() || 'normal',
						})
						.run();
					break;
				case 'foreColor':
					chain()
						.setMark('textStyle', { color: val, [DYNAMIC_COLOR_ATTR]: dynamicKey })
						.run();
					break;
				case 'hiliteColor':
					chain()
						.setMark('textStyle', {
							backgroundColor: val,
							[DYNAMIC_BACKGROUND_COLOR_ATTR]: dynamicKey,
						})
						.run();
					break;
				case 'justifyLeft':
					chain().setTextAlign('left').run();
					break;
				case 'justifyCenter':
					chain().setTextAlign('center').run();
					break;
				case 'justifyRight':
					chain().setTextAlign('right').run();
					break;
				case 'insertOrderedList':
					chain().toggleOrderedList().run();
					break;
				case 'insertUnorderedList':
					chain().toggleBulletList().run();
					break;
				case 'insertHorizontalRule':
					chain().setHorizontalRule().run();
					break;
				case 'createLink': {
					const link = val as LinkParams;
					const href = link.link.trim();
					if (!href) break;
					// `style` is an attribute InlineLink adds, outside setLink's typings.
					const attrs = {
						href,
						target: link.blank ? '_blank' : null,
						rel: link.blank ? 'noopener noreferrer' : null,
						style: `color: inherit; text-decoration: ${link.underline ? 'underline' : 'none'};`,
					} as Parameters<ReturnType<typeof chain>['setLink']>[0];
					chain().extendMarkRange('link').setLink(attrs).run();
					break;
				}
				case 'unlink':
					chain().extendMarkRange('link').unsetLink().run();
					break;
				case 'removeFormat':
					chain().unsetAllMarks().run();
					break;
				case 'insertMergeTag': {
					const html = typeof val?.html === 'string' ? val.html : '';
					if (!html) break;
					const content = enabledMergeTagsBadge
						? MergeTagBadge.transform(html, (+new Date()).toString())
						: html;
					chain()
						.insertContent(content, { parseOptions: { preserveWhitespace: 'full' } })
						.run();
					break;
				}
				default:
					console.warn('Unsupported toolbar command', cmd);
			}
		},
		[editor, enabledMergeTagsBadge],
	);

	const selectionRange = getSelectionDomRange(editor);

	const enabledTools = toolbar?.tools ?? DEFAULT_RTE_TOOLS;

	const tools = enabledTools.flatMap(tool => {
		switch (tool) {
			case AvailableTools.MergeTags:
				return [
					<MergeTags
						key={tool}
						execCommand={execCommand}
					/>,
				];
			case AvailableTools.FontFamily:
				return [
					<FontFamily
						key={tool}
						execCommand={execCommand}
						getPopupContainer={getPopoverMountNode}
					/>,
				];
			case AvailableTools.FontSize:
				return [
					<FontSize
						key={tool}
						execCommand={execCommand}
						getPopupContainer={getPopoverMountNode}
					/>,
					<LineHeightTool
						key={`${tool}-line-height`}
						getPopupContainer={getPopoverMountNode}
					/>,
					<LetterSpacingTool
						key={`${tool}-letter-spacing`}
						getPopupContainer={getPopoverMountNode}
					/>,
				];
			case AvailableTools.TextTransform:
				return [
					<TextTransform
						key={tool}
						execCommand={execCommand}
						getPopupContainer={getPopoverMountNode}
					/>,
				];
			case AvailableTools.TextWrap:
				return [
					<TextWrap
						key={tool}
						execCommand={execCommand}
						getPopupContainer={getPopoverMountNode}
					/>,
				];
			case AvailableTools.Bold:
				return [
					<Bold
						key={tool}
						currentRange={selectionRange}
						onChange={() => execCommand('bold')}
					/>,
				];
			case AvailableTools.Italic:
				return [
					<Italic
						key={tool}
						currentRange={selectionRange}
						onChange={() => execCommand('italic')}
					/>,
				];
			case AvailableTools.StrikeThrough:
				return [
					<StrikeThrough
						key={tool}
						currentRange={selectionRange}
						onChange={() => execCommand('strikeThrough')}
					/>,
				];
			case AvailableTools.Underline:
				return [
					<Underline
						key={tool}
						currentRange={selectionRange}
						onChange={() => execCommand('underline')}
					/>,
				];
			case AvailableTools.IconFontColor:
				return [
					<IconFontColor
						key={tool}
						selectionRange={selectionRange}
						execCommand={execCommand}
						getPopoverMountNode={getPopoverMountNode}
						brandColors={brandColors}
					/>,
				];
			case AvailableTools.IconBgColor:
				return [
					<IconBgColor
						key={tool}
						selectionRange={selectionRange}
						execCommand={execCommand}
						getPopoverMountNode={getPopoverMountNode}
						brandColors={brandColors}
					/>,
				];
			case AvailableTools.TableCellBgColor:
				return [
					<TableCellBgColor
						key={tool}
						selectionRange={selectionRange}
						getPopoverMountNode={getPopoverMountNode}
						brandColors={brandColors}
					/>,
				];
			case AvailableTools.Link:
				return [
					<Link
						key={`${tool}-link`}
						currentRange={selectionRange}
						onChange={values => execCommand('createLink', values)}
						getPopupContainer={getPopoverMountNode}
					/>,
					<Unlink
						key={`${tool}-unlink`}
						currentRange={selectionRange}
						onChange={() => execCommand('unlink')}
					/>,
				];
			case AvailableTools.Justify:
				return [
					<ToolItem
						key={`${tool}-justify-left`}
						onClick={() => execCommand('justifyLeft')}
						icon={<IconFont iconName='icon-align-left' />}
						title={t('Align Left')}
					/>,
					<ToolItem
						key={`${tool}-justify-center`}
						onClick={() => execCommand('justifyCenter')}
						icon={<IconFont iconName='icon-align-center' />}
						title={t('Align Center')}
					/>,
					<ToolItem
						key={`${tool}-justify-right`}
						onClick={() => execCommand('justifyRight')}
						icon={<IconFont iconName='icon-align-right' />}
						title={t('Align Right')}
					/>,
				];
			case AvailableTools.Lists:
				return [
					<ToolItem
						key={`${tool}-ordered-list`}
						onClick={() => execCommand('insertOrderedList')}
						icon={<IconFont iconName='icon-list-ol' />}
						title={t('Numbered List')}
					/>,
					<ToolItem
						key={`${tool}-unordered-list`}
						onClick={() => execCommand('insertUnorderedList')}
						icon={<IconFont iconName='icon-list-ul' />}
						title={t('Bulleted List')}
					/>,
				];
			case AvailableTools.HorizontalRule:
				return [
					<ToolItem
						key={tool}
						onClick={() => execCommand('insertHorizontalRule')}
						icon={<IconFont iconName='icon-line' />}
						title={t('Line')}
					/>,
				];
			case AvailableTools.RemoveFormat:
				return [
					<ToolItem
						key={tool}
						onClick={() => execCommand('removeFormat')}
						icon={<IconFont iconName='icon-clear' />}
						title={t('Remove Format')}
					/>,
				];
			default:
				console.error('Not existing tool', tool);
				throw new Error(`Not existing tool ${tool}`);
		}
	});

	return (
		<TiptapEditorContext.Provider value={editor}>
			<div
				id={RICH_TEXT_TOOL_BAR}
				style={{
					display: 'flex',
					flexWrap: 'nowrap',
					alignItems: 'center',
					justifyContent: 'center',
					position: 'relative',
				}}
			>
				<div
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					{tools.map((tool, index) => (
						<React.Fragment key={`tool-${index}`}>
							{tool}
							{index < tools.length - 1 && (
								<div className='bay-email-extensions-divider' />
							)}
						</React.Fragment>
					))}
				</div>
				{toolbar?.suffix && <div style={{ marginLeft: 8 }}>{toolbar.suffix(execCommand)}</div>}
			</div>
		</TiptapEditorContext.Provider>
	);
}
